import {clamp} from '@benchling/js-utils';
import Backbone, {ViewOptions} from 'backbone';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import $ from 'jquery';
import React from 'react';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import Trim from 'analyses/alignment/Trim';
import {TraceData, VoteData} from 'analyses/Analyses';
import * as analytics from 'analytics/Analytics';
import {NucleotideType} from 'biojs/BioConstants';
import {Part} from 'biojs/Part';
import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {ComponentsSequence} from 'components/Sequence';
import {Sequence} from 'components/Sequence';
import * as MapsConstants from 'constants/MapsConstants';
import {AllPolymerLayouts, LayeringMode, SliceComponentName} from 'constants/MapsConstants';
import {SequenceType} from 'constants/SequenceConstants';
import * as context from 'Context';
import Protein from 'molbio/aa-sequences/AaSequence';
import ProteinAlignmentCDRAnnotationsView from 'molbio/aa-sequences/AaSequenceAlignmentCDRAnnotationsView';
import ProteinAnnotationsView from 'molbio/aa-sequences/AaSequenceAnnotationsView';
import ProteinCDRView from 'molbio/aa-sequences/AaSequenceCDRAnnotationsView';
import * as ProteinContextMenus from 'molbio/aa-sequences/AaSequenceContextMenus';
import ProteinAlignmentAnnotationsView from 'molbio/aa-sequences/alignments/AaSequenceAlignmentAnnotationsView';
import ProteinAlignmentAxisView from 'molbio/aa-sequences/alignments/AaSequenceAlignmentAxisView';
import {getAminoAcidMatrix} from 'molbio/aa-sequences/alignments/AaSequenceAlignmentStatsUtil';
import {getIdentityValues} from 'molbio/aa-sequences/alignments/AaSequenceAlignmentUtil';
import AminoAcids from 'molbio/aa-sequences/AminoAcids';
import {ProteinAnnotationVersion, ProteinPart} from 'molbio/aa-sequences/util/AaSequenceConstants';
import * as ProteinProperties from 'molbio/aa-sequences/util/AaSequenceProperties';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import {
  SINGLE_ANNOTATION_BORDER,
  SINGLE_ANNOTATION_HEIGHT,
  LINK_TO_PROTEIN_ANNOTATION_VMARGIN,
} from 'molbio/maps/BiointelligentLinksConstants';
import ProteinAxisView from 'molbio/maps/linear/AaSequenceAxisView';
import ProteinLiabilitySites from 'molbio/maps/linear/AaSequenceLiabilitySites';
import ProteinPartAnnotations, {
  getProteinPartAnnotationsLayersDict,
} from 'molbio/maps/linear/AaSequencePartAnnotations';
import AlignmentCommentsView from 'molbio/maps/linear/AlignmentCommentsView';
import AminoAcidsSearchResults from 'molbio/maps/linear/AminoAcidsSearchResults';
import AntibodyNumberingSlice from 'molbio/maps/linear/AntibodyNumbering';
import BiointelligentLinkAnnotations from 'molbio/maps/linear/BiointelligentLinkAnnotations';
import BiointelligentLinkHighlightView from 'molbio/maps/linear/BiointelligentLinkHighlightView';
import {getVisibleBiointelligentLinkTypesForHighlights} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import CodonOptimizationSelection from 'molbio/maps/linear/CodonOptimizationSelection';
import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import CutSitesView, {CutSitesViewOptions} from 'molbio/maps/linear/CutSitesView';
import DNAView from 'molbio/maps/linear/DNAView';
import EmptyPlaceholderView, {EmptyPlaceholderViewOptions} from 'molbio/maps/linear/EmptyPlaceholderView';
import GapToRealAxisView from 'molbio/maps/linear/GapToRealAxisView';
import GoldenGateCutSiteView from 'molbio/maps/linear/GoldenGateCutSiteView';
import HomologyRegionAxisView from 'molbio/maps/linear/HomologyRegionAxisView';
import {MinimalSliceView} from 'molbio/maps/linear/InfiniteScroller';
import LayeredReactWrapperView, {
  LayeredReactWrapperViewOptions,
} from 'molbio/maps/linear/LayeredReactWrapperView';
import {LayeredViewOptions} from 'molbio/maps/linear/LayeredView';
import {getLayersDict, LayersDict} from 'molbio/maps/linear/LayeringHelpers';
import LineView from 'molbio/maps/linear/LineView';
import ModifiedNucleotides, {MODIFIED_NUCLEOTIDES_BOX_HEIGHT} from 'molbio/maps/linear/ModifiedNucleotides';
import ModifiedNucleotideSummary from 'molbio/maps/linear/ModifiedNucleotideSummary';
import NucleotidePositionHighlight from 'molbio/maps/linear/NucleotidePositionHighlight';
import NucleotidesAxisOverlay from 'molbio/maps/linear/NucleotidesAxisOverlay';
import NucleotidesSearchResults from 'molbio/maps/linear/NucleotidesSearchResults';
import ORFsView from 'molbio/maps/linear/ORFsView';
import PAMSitesView, {PAMSitesViewOptions} from 'molbio/maps/linear/PAMSitesView';
import PrimerAssemblyMismatchView, {
  PrimerAssemblyMismatchViewOptions,
} from 'molbio/maps/linear/PrimerAssemblyMismatchView';
import {
  getPrimerBasesVisible,
  getPrimerType,
  getSliceHasAnyPrimerMods,
  getSlicePrimers,
} from 'molbio/maps/linear/PrimersHelpers';
import {PrimersView} from 'molbio/maps/linear/PrimersView';
import ReactWrapperView, {ReactWrapperViewOptions} from 'molbio/maps/linear/ReactWrapperView';
import ReadingFramesView from 'molbio/maps/linear/ReadingFramesView';
import ReverseBiointelligentLinkHighlightViewSpace from 'molbio/maps/linear/ReverseBiointelligentLinkHighlightViewSpace';
import SelectionHiddenWrapperView from 'molbio/maps/linear/SelectionHiddenWrapperView';
import SequenceAnnotationsView from 'molbio/maps/linear/SequenceAnnotationsView';
import SequenceAxisView from 'molbio/maps/linear/SequenceAxisView';
import Slice from 'molbio/maps/linear/Slice';
import TitleView, {TitleViewOptions} from 'molbio/maps/linear/TitleView';
import TooltipBarGraph from 'molbio/maps/linear/TooltipBarGraph';
import TraceView from 'molbio/maps/linear/TraceView';
import {TranscriptionArrow} from 'molbio/maps/linear/TranscriptionArrow';
import {getTranscriptionAnnotationsLayersDict} from 'molbio/maps/linear/TranscriptionHelpers';
import {TranscriptionLine} from 'molbio/maps/linear/TranscriptionLine';
import {getSingleTranslationHeight} from 'molbio/maps/linear/TranslationDisplayUtil';
import TranslationsAsAnnotationsView from 'molbio/maps/linear/TranslationsAsAnnotationsView';
import TranslationsView from 'molbio/maps/linear/TranslationsView';
import Votes from 'molbio/maps/linear/Votes';
import {ProteinLinearMapState, SequenceLinearMapState} from 'molbio/maps/MapStates';
import {getSinglePrimerHeight} from 'molbio/maps/PrimersViewConstants';
import Range from 'molbio/maps/selectables/Range';
import {NonEmptySelection, Selection} from 'molbio/maps/selectables/Selection';
import SequenceMapUtil from 'molbio/maps/SequenceMapUtil';
import Type from 'molbio/maps/Type';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import {
  BaseSelectionMethod,
  trackSequenceMapBaseSelection,
  trackSequenceMapClick,
  trackSequenceMapRightClickWithSelectedBases,
} from 'molbio/SequenceAnalyticsHelper';
import * as sequtil from 'molbio/SeqUtil';
import ProteinAlignmentTabState from 'molbio/tabstates/AaSequenceAlignmentTabState';
import {TranscriptionMetadata} from 'molbio/TranscriptionsUtil';
import Translation from 'molbio/translations/backbone/Translation';
import {BufferState, TabState} from 'Navigation';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {MAXIMISE} from 'settings/visibility-options/VisibilityOptions';
import addTestAttrsToD3Selection from 'test/lib/cypress/addTestAttrsToD3Selection';
import {MakeFieldsRequired} from 'util/Types';

const AXIS_CLASSES = ['sequenceAxis', 'gapToRealAxis'];

const DEFAULT_MIN_SLICE_HEIGHT = 25;
const EXPANDED_MIN_SLICE_HEIGHT = 75;

// Only used for protein alignments, which provide info through data rather than a mapstate
interface SliceViewData {
  row: {proteinVersion: {annotations: Array<ProteinAnnotationVersion>}};
}

interface Rectangle {
  start: number;
  end: number;
}

export interface SliceViewOptions extends ViewOptions<SliceViewModel> {
  selectionOptions?: unknown;
  mapType: MapsConstants.SliceMapType;
  tabstate: TabState;
  focusableParent?: HTMLElement | JQuery;
  slice: Slice;
  range?: Range;
  selection: Selection;
  trim?: Trim;
  data?: SliceViewData;
  visibilityOptions?: vo.VisibilityOptions;
  // Components that should be shown on the map, in top->down vertical order
  y: Array<SliceComponentName>;
  // Components that should be shown on the map, in top->bottom z-axis order
  z: Array<SliceComponentName>;
  svgSupportsForeignObject?: boolean;
  traceData?: TraceData | null; // Only for DNA alignments
  voteData?: VoteData | null; // Only for DNA alignments
}

interface Rect {
  start: number;
  end: number;
}

// Includes: MiniMapState, ProteinAlignmentTabState, AlignmentMapState, ProteinLinearMapState, SequenceLinearMapState
export interface SliceViewModel extends Backbone.Model {
  polymer?(): Sequence | Protein;
}

type PolymerSliceViewModel = MakeFieldsRequired<SliceViewModel, 'polymer'>;

function assertModelHasPolymer(model: SliceViewModel): asserts model is PolymerSliceViewModel {
  if (model.polymer == null) {
    throw new Error('Cannot call method on model without polymer. Try overriding in BasicSliceView');
  }
}

interface SelectLinesDatum {
  type: 'start' | 'end';
  x: number;
}

declare class SliceComponent extends Backbone.View<Backbone.Model> {
  boxHeight(): number;

  updateForAdjust(): void;

  update(trigger?: boolean): void;

  die(): void;

  render(trigger?: boolean): Backbone.View<Backbone.Model>;

  bottomMarginBleed(): number;
}

/**
 * A Backbone.View to render a slice of a sequence / protein / sequence alignment / protein alignment.
 *
 * Most of SliceView's implementation should treat model as an opaque Backbone model that has a
 * `polymerLayout` attribute (in one of the shapes defined in MapsConstants.ts).
 * HOWEVER, the following methods assume model is a Backbone model with a `polymer` method (returning a
 * Protein or Sequence) - subclasses such as BasicSliceView should override these methods:
 *
 *   createSVGElement, isCircular, canEditSelection, openSelectionContextMenu,
 *   handleDoubleClick, getPolymerLength, getResidueSlice, storeSliceHeightOnModel, setSVGWidth
 *
 */
class SliceView extends Backbone.View<SliceViewModel> implements MinimalSliceView {
  override tagName!: string;
  override className!: string;
  mapType!: MapsConstants.SliceMapType;
  slice!: Slice;
  override el!: SVGSVGElement;
  focusableParent!: HTMLElement | JQuery | null;
  selectionOptions!: unknown;
  tabstate!: TabState;
  range!: Range | null;
  selection!: Selection;
  trim?: Trim;
  data?: SliceViewData;
  visibilityOptions!: vo.VisibilityOptions;
  y!: Array<SliceComponentName>;
  z!: Array<SliceComponentName>;
  hasRendered!: boolean;
  dontUpdate?: boolean;
  yIndexMap!: {-readonly [key in keyof typeof SliceComponentName]?: number};
  yDeps!: {-readonly [key in keyof typeof SliceComponentName]?: Array<SliceComponentName>};
  components!: {-readonly [key in keyof typeof SliceComponentName]?: SliceComponent};
  zComponents!: Array<SliceComponent>;
  // Order in which components get rendered (if they're dependent)
  rComponents!: Array<SliceComponent>;
  override model!: SliceViewModel;

  // Attributes defined in subclass but referenced in superclass
  _polymerResidues?: string;
  _forAlignmentPNGExport?: boolean;

  // Attributes used in selection logic
  oldRectangle!: Array<Rectangle>;
  oldLineData!: Array<SelectLinesDatum>;
  oldBoxHeight!: number | undefined;
  oldEndpoints!: undefined;
  oldEmpty!: boolean | undefined;
  oldLineStart?: number;
  oldLineEnd?: number;
  oldWidthLines?: number;
  oldSelectStart?: number;
  oldSelectEnd?: number;
  oldWidth?: number;
  oldCanPCR?: boolean;
  oldMaxBases?: number;

  static initClass(): void {
    this.prototype.tagName = 'svg';
    this.prototype.className = 'slice';

    this.prototype.events = {
      // @ts-expect-error Mousedown isn't in Backbone events
      mousedown: 'updateSelection',
      mousemove: 'updateSelection',
      mouseup: 'updateSelection',
      contextmenu: 'updateSelection',
      dblclick: 'updateSelection',
    };
  }

  static createSVGElement(): SVGElement {
    // Backbone does not namespace the tagName correctly, so a caller may create the `el` associated with
    // an instantiation of a SliceView through this method.
    const el = document.createElementNS('http://www.w3.org/2000/svg', this.prototype.tagName);
    $(el).addClass(this.prototype.className);
    return el;
  }

  canEditSelection(): boolean {
    assertModelHasPolymer(this.model);
    const polymer = this.model.polymer();
    return polymer.allowEdits() && polymer.isSelectionDeletable();
  }

  openSelectionContextMenu(e: MouseEvent): void {
    assertModelHasPolymer(this.model);
    const polymer = this.model.polymer();
    if (polymer instanceof Protein) {
      ProteinContextMenus.openSelectionContextMenu(e, polymer);
    } else if (polymer.isOligo()) {
      selectioncontextmenu.open(
        e,
        polymer,
        selectioncontextmenu.OLIGO_SELECTION_MENU,
        this.contextMenuData()
      );
    } else {
      const choices = selectioncontextmenu.getSelectionMenu({
        alignment: this.mapType === 'alignment',
      });
      selectioncontextmenu.open(e, polymer, choices, this.contextMenuData());
    }
  }

  handleDoubleClick(e: unknown): void {
    assertModelHasPolymer(this.model);
    const sequence = this.model.polymer();
    sequence.handleDoubleClick(this.slice.start(), this.slice.end());
  }

  getPolymerLength(): number {
    assertModelHasPolymer(this.model);
    return this.model.polymer().length();
  }

  getResidueSlice(): string {
    assertModelHasPolymer(this.model);
    return this.model.polymer().residueSlice(this.slice.start(), this.slice.end());
  }

  triggerHeightChange(height: number): void {
    this.slice.set('height', height);
  }

  setSVGWidth(): void {
    const width = this.model
      .get('layout')
      .DNA.get('type')
      .width(this.slice.end() - this.slice.start());
    const padding = this.model.get('layout').DNA.get('type').letterSpacing;
    d3.select(this.el).attr('width', padding + width + 1);
  }

  height(): number {
    return this.slice.get('height')!;
  }

  setFocusableParent(focusableParent: HTMLElement | JQuery): void {
    this.focusableParent = focusableParent;
  }

  constructor(options: SliceViewOptions) {
    super(options);
  }

  override initialize(options: SliceViewOptions): void {
    this.selectionOptions = options.selectionOptions || {};
    this.mapType = options.mapType;
    this.tabstate = options.tabstate;
    this.focusableParent = options.focusableParent || null;
    this.slice = options.slice;
    this.range = options.range != null ? options.range : null;
    this.selection = options.selection;
    this.trim = options.trim;
    this.data = options.data;
    this.visibilityOptions = options.visibilityOptions || this.model.get('visibilityOptions');
    this.y = options.y;
    this.z = options.z;

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.blinkCursor = debounce(this.blinkCursor, 100);
    this.oldRectangle = [];
    this.oldLineData = [];
    this.oldBoxHeight = undefined;
    this.oldEndpoints = undefined;
    this.oldEmpty = undefined;
    this.hasRendered = false;

    this.yIndexMap = {};
    for (let i = 0; i < this.y.length; i++) {
      this.yIndexMap[this.y[i]] = i;
    }

    // Render things that touch the DNA last, from the closest to the DNA to the furthest.
    const [dnaComponents, normalComponents] = _.partition(this.y, (className) =>
      [
        'forwardPamSites',
        'cutSites',
        'transcriptionArrow',
        'reversePamSites',
        'modifiedNucleotides',
      ].includes(className)
    );
    // Render DNA first in case some are dependent (might not be needed anymore, see https://github.com/benchling/aurelia/commit/de9af35d7078e9f4f14fb1cc5e743a46973e1e60)
    const r = normalComponents.concat(
      _.sortBy(dnaComponents, (className) => Math.abs(this.yIndexMap[className]! - this.yIndexMap.dna!))
    );

    // dnaComponents need to be redrawn if their y-position changes or if DNA y-position changes.
    this.yDeps = {};
    for (const dnaComponent of dnaComponents) {
      this.yDeps[dnaComponent] = [dnaComponent];
    }
    this.yDeps.dna = dnaComponents;

    const componentMethods: {
      [key in keyof typeof SliceComponentName]: () => Component;
    } = {
      title(this: SliceView): TitleView {
        return new TitleView({
          model: this.model,
          slice: this.slice,
          bufferCentred: false,
          name: SliceComponentName.title,
        } as TitleViewOptions);
      },
      emptyPlaceholder(this: SliceView): EmptyPlaceholderView {
        const emptyPlaceholderOptions: EmptyPlaceholderViewOptions = {
          model: this.model,
          slice: this.slice,
          name: SliceComponentName.emptyPlaceholder,
          tabstate: this.tabstate,
        };
        return new EmptyPlaceholderView(emptyPlaceholderOptions);
      },
      dna(this: SliceView): DNAView {
        return new DNAView({
          model: this.model,
          // @ts-expect-error TODO loosen model requirements
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          svgSupportsForeignObject: options.svgSupportsForeignObject,
          traceData: options.traceData,
          name: SliceComponentName.dna,
        });
      },
      aminoAcids(this: SliceView): ReactWrapperView<'aminoAcids'> {
        const aminoAcidsOptions: ReactWrapperViewOptions<'aminoAcids'> = {
          className: 'aminoAcids residues',
          setting: vo.AMINO_ACIDS.name,
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'aminoAcids',
          disableMinimize: true,
          boxHeight: (layout) => SequenceMapUtil.aminoAcidBoxHeight(layout),
          renderReact: (layout) => {
            const aminoAcids = this.getResidueSlice();
            return (
              <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
                <AminoAcids
                  aminoAcids={aminoAcids}
                  layout={layout}
                  sliceStart={this.slice.start()}
                  xFn={this.slice.x()}
                />
              </ComponentWrapper>
            );
          },
          // For Type.largest (24px), 4px was observed as the descent below the baseline.
          bottomMarginBleed: () => 4,
          tabstate: this.tabstate,
          name: SliceComponentName.aminoAcids,
        };
        return new ReactWrapperView(aminoAcidsOptions);
      },
      antibodyCDRAnnotations(this: SliceView): ProteinCDRView {
        return new ProteinCDRView({
          // @ts-expect-error TODO loosen model requirements
          model: this.model as ProteinLinearMapState | ProteinAlignmentTabState,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.antibodyCDRAnnotations,
        });
      },
      antibodyAlignmentCDRAnnotations(this: SliceView): ProteinAlignmentCDRAnnotationsView {
        return new ProteinAlignmentCDRAnnotationsView({
          // @ts-expect-error TODO loosen model requirements
          model: this.model as ProteinLinearMapState | ProteinAlignmentTabState,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          annotations: this.data!.row.proteinVersion.annotations,
          aminoAcids: this._polymerResidues!,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.antibodyAlignmentCDRAnnotations,
        });
      },
      cutSites(this: SliceView): CutSitesView {
        const viewOptions: CutSitesViewOptions = {
          model: this.model,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.cutSites,
        };
        return new CutSitesView(viewOptions);
      },
      forwardPamSites(this: SliceView): PAMSitesView {
        const forwardPamSitesOptions: PAMSitesViewOptions = {
          model: this.model,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          strand: 1,
          componentClassName: SliceComponentName.forwardPamSites,
          name: SliceComponentName.forwardPamSites,
        };
        return new PAMSitesView(forwardPamSitesOptions);
      },
      reversePamSites(this: SliceView): PAMSitesView {
        const reversePamSitesOptions: PAMSitesViewOptions = {
          model: this.model,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          strand: -1,
          componentClassName: SliceComponentName.reversePamSites,
          name: SliceComponentName.reversePamSites,
        };
        return new PAMSitesView(reversePamSitesOptions);
      },
      nucleotidesSearch(this: SliceView): ReactWrapperView<'nucleotidesSearch'> {
        const dnaLayout = this.model.get('layout').DNA;
        const nucleotideSearchResultsOptions: ReactWrapperViewOptions<'nucleotidesSearch'> = {
          className: 'nucleotidesSearch',
          setting: vo.SEARCH_BASES.name,
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'nucleotidesSearch',
          disableMinimize: true,
          boxHeight: ({vMargin}) => vMargin,
          renderReact: (layout) => (
            <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
              <NucleotidesSearchResults
                sliceStart={this.slice.start()}
                sliceEnd={this.slice.end()}
                xFn={this.slice.x()}
                sequence={this.model.polymer!() as Sequence}
                showingComplement={dnaLayout.get('complement')}
                basesHeight={dnaLayout.height() + dnaLayout.get('spacer')}
              />
            </ComponentWrapper>
          ),
          additionalListeners: [
            {other: SearchDNAData.get().get('results'), event: 'reset change:selected'},
            {other: dnaLayout, event: 'change'},
          ],
          tabstate: this.tabstate,
          name: SliceComponentName.nucleotidesSearch,
        };
        return new ReactWrapperView(nucleotideSearchResultsOptions);
      },
      translations(
        this: SliceView
      ):
        | LayeredReactWrapperView<typeof MapsConstants.LINEAR_LAYER_FULL, Translation, 'translations'>
        | TranslationsAsAnnotationsView {
        if (this.mapType === MapsConstants.SliceMapType.minimap) {
          return new TranslationsAsAnnotationsView({
            model: this.model,
            tabstate: this.tabstate,
            visibilityOptions: this.visibilityOptions,
            slice: this.slice,
            parent: this,
            name: SliceComponentName.translations,
          });
        } else {
          const indexType = this.model.get('layout').Translations.get('indexType');
          const fontType = this.model.get('layout').Translations.get('single').get('type');
          const translationsOptions: LayeredReactWrapperViewOptions<
            typeof MapsConstants.LINEAR_LAYER_FULL,
            Translation,
            'translations'
          > = {
            name: SliceComponentName.translations,
            className: 'translations',
            polymerLayoutName: 'translations',
            disableMinimize: false,
            visibilityOptions: this.visibilityOptions,
            setting: vo.TRANSLATIONS.name,
            secondarySettings: [vo.AA_INDICES.name],
            additionalListeners: [
              {
                other: this.model.get('sequence').translations(),
                event: 'change',
              },
            ],
            model: this.model,
            layeringMode: MapsConstants.LINEAR_LAYER_FULL,
            singleHeight: () =>
              getSingleTranslationHeight(
                fontType.height(),
                true,
                indexType.height(),
                this.visibilityOptions.getOption(vo.AA_INDICES.name)!.get('visible')
              ),
            getLayersDict: (sliceModels, layeringMode) => getLayersDict(sliceModels, layeringMode),
            getSliceModels: () =>
              this.model
                .get('sequence')
                .translations()
                .filter((t: Translation) =>
                  sequtil.isRangeOverlap(t.start(), t.end(), this.slice.start(), this.slice.end())
                ),
            renderReact: (
              layout: AllPolymerLayouts['translations'],
              redraw: () => void,
              sliceModels: Array<Translation>,
              layersDict: LayersDict,
              layeringMode: typeof MapsConstants.LINEAR_LAYER_FULL,
              yFn: ScaleLinear<number, number>
            ) => (
              <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={true}>
                <TranslationsView
                  colorScheme={this.visibilityOptions
                    .getOption(vo.TRANSLATIONS.name)
                    ?.getSetting(vo.getTranslationColoringSetting().name)
                    ?.get('value')}
                  sliceTranslations={sliceModels}
                  sliceStart={this.slice.start()}
                  sliceEnd={this.slice.end()}
                  aminoAcidIndicesVisible={this.visibilityOptions
                    .getOption(vo.AA_INDICES.name)!
                    .get('visible')}
                  layersDict={layersDict}
                  layeringMode={layeringMode}
                  sequence={this.model.get('sequence')}
                  openContextMenu={(e: React.MouseEvent, translation: Translation) => {
                    const sequence = this.model.get('sequence');
                    const contextMenuOptions = selectioncontextmenu.TRANSLATION_MENU;
                    const contextMenuData = {
                      translation,
                      sequence,
                      mapstate: this.model,
                    };
                    selectioncontextmenu.open(e.nativeEvent, sequence, contextMenuOptions, contextMenuData);
                  }}
                  redraw={redraw}
                  xFn={this.slice.x()}
                  yFn={yFn}
                  indexType={indexType}
                  fontType={fontType}
                />
              </ComponentWrapper>
            ),
            tabstate: this.tabstate,
          };
          return new LayeredReactWrapperView(translationsOptions);
        }
      },
      readingFrames(this: SliceView): ReadingFramesView {
        return new ReadingFramesView({
          model: this.model,
          // @ts-expect-error
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          range: this.range,
          name: SliceComponentName.readingFrames,
        });
      },
      annotations(this: SliceView): SequenceAnnotationsView {
        return new SequenceAnnotationsView({
          model: this.model,
          // @ts-expect-error TODO loosen model requirements
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.annotations,
        });
      },
      primers(
        this: SliceView
      ): LayeredReactWrapperView<typeof MapsConstants.LINEAR_LAYER_FULL, SequencePrimer, 'primers'> {
        const primersOptions: LayeredReactWrapperViewOptions<
          typeof MapsConstants.LINEAR_LAYER_FULL,
          SequencePrimer,
          'primers'
        > = {
          className: 'primers',
          polymerLayoutName: 'primers',
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          secondarySettings: [vo.PRIMER_BASES.name],
          setting: vo.PRIMERS.name,
          model: this.model,
          additionalListeners: [
            {
              other: this.model.get('sequence').sequencePrimers(),
              event: 'change:hover change:selected',
            },
          ],
          layeringMode: MapsConstants.LINEAR_LAYER_FULL,
          singleHeight: () =>
            getSinglePrimerHeight(
              getPrimerType(this.visibilityOptions, this.model.get('layout').DNA.get('type')),
              getSliceHasAnyPrimerMods(
                this.model.get('sequence'),
                this.slice.start(),
                this.slice.end(),
                getPrimerBasesVisible(this.visibilityOptions)
              ),
              getPrimerBasesVisible(this.visibilityOptions)
            ),
          getLayersDict: (sliceModels, layeringMode) => getLayersDict(sliceModels, layeringMode),
          getSliceModels: () =>
            getSlicePrimers(
              this.model.get('sequence'),
              this.slice.start(),
              this.slice.end(),
              getPrimerBasesVisible(this.visibilityOptions)
            ),
          renderReact: (
            layout: AllPolymerLayouts['primers'],
            redraw: () => void,
            sliceModels: Array<SequencePrimer>,
            layersDict: LayersDict,
            layeringMode: LayeringMode,
            yFn: ScaleLinear<number, number>
          ) => (
            <PrimersView
              xFn={this.slice.x()}
              yFn={yFn}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequence={this.model.get('sequence')}
              contextMenuOptions={selectioncontextmenu.PRIMER_MENU}
              polymerLayout={layout}
              primerBasesVisible={getPrimerBasesVisible(this.visibilityOptions)}
              sliceHasAnyMods={getSliceHasAnyPrimerMods(
                this.model.get('sequence'),
                this.slice.start(),
                this.slice.end(),
                getPrimerBasesVisible(this.visibilityOptions)
              )}
              primerType={getPrimerType(this.visibilityOptions, this.model.get('layout').DNA.get('type'))}
              residuesType={this.model.get('layout').DNA.get('type')}
              mapType={this.mapType}
              redraw={redraw}
              slicePrimers={sliceModels}
              layersDict={layersDict}
              layeringMode={layeringMode}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.primers,
        };
        return new LayeredReactWrapperView(primersOptions);
      },
      forwardPrimers(
        this: SliceView
      ): LayeredReactWrapperView<typeof MapsConstants.LINEAR_LAYER_FULL, SequencePrimer, 'primers'> {
        const forwardPrimersOptions: LayeredReactWrapperViewOptions<
          typeof MapsConstants.LINEAR_LAYER_FULL,
          SequencePrimer,
          'primers'
        > = {
          className: 'forwardPrimers',
          polymerLayoutName: 'primers',
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          secondarySettings: [vo.PRIMER_BASES.name],
          setting: vo.PRIMERS.name,
          model: this.model,
          additionalListeners: [
            {
              other: this.model.get('sequence').sequencePrimers(),
              event: 'change:hover change:selected',
            },
          ],
          layeringMode: MapsConstants.LINEAR_LAYER_FULL,
          singleHeight: () =>
            getSinglePrimerHeight(
              getPrimerType(this.visibilityOptions, this.model.get('layout').DNA.get('type')),
              getSliceHasAnyPrimerMods(
                this.model.get('sequence'),
                this.slice.start(),
                this.slice.end(),
                getPrimerBasesVisible(this.visibilityOptions)
              ),
              getPrimerBasesVisible(this.visibilityOptions)
            ),
          getLayersDict: (sliceModels, layeringMode) => getLayersDict(sliceModels, layeringMode),
          getSliceModels: () =>
            getSlicePrimers(
              this.model.get('sequence'),
              this.slice.start(),
              this.slice.end(),
              getPrimerBasesVisible(this.visibilityOptions),
              1
            ),
          renderReact: (
            layout: AllPolymerLayouts['primers'],
            redraw: () => void,
            sliceModels: Array<SequencePrimer>,
            layersDict: LayersDict,
            layeringMode: LayeringMode,
            yFn: ScaleLinear<number, number>
          ) => (
            <PrimersView
              xFn={this.slice.x()}
              yFn={yFn}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequence={this.model.get('sequence')}
              contextMenuOptions={selectioncontextmenu.PRIMER_MENU}
              polymerLayout={layout}
              primerBasesVisible={getPrimerBasesVisible(this.visibilityOptions)}
              sliceHasAnyMods={getSliceHasAnyPrimerMods(
                this.model.get('sequence'),
                this.slice.start(),
                this.slice.end(),
                getPrimerBasesVisible(this.visibilityOptions)
              )}
              primerType={getPrimerType(this.visibilityOptions, this.model.get('layout').DNA.get('type'))}
              residuesType={this.model.get('layout').DNA.get('type')}
              mapType={this.mapType}
              redraw={redraw}
              slicePrimers={sliceModels}
              layersDict={layersDict}
              layeringMode={layeringMode}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.forwardPrimers,
        };
        return new LayeredReactWrapperView(forwardPrimersOptions);
      },
      reversePrimers(
        this: SliceView
      ): LayeredReactWrapperView<typeof MapsConstants.LINEAR_LAYER_FULL, SequencePrimer, 'primers'> {
        const reversePrimersOptions: LayeredReactWrapperViewOptions<
          typeof MapsConstants.LINEAR_LAYER_FULL,
          SequencePrimer,
          'primers'
        > = {
          className: 'reversePrimers',
          polymerLayoutName: 'primers',
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          secondarySettings: [vo.PRIMER_BASES.name],
          setting: vo.PRIMERS.name,
          model: this.model,
          additionalListeners: [
            {
              other: this.model.get('sequence').sequencePrimers(),
              event: 'change:hover change:selected',
            },
          ],
          layeringMode: MapsConstants.LINEAR_LAYER_FULL,
          singleHeight: () =>
            getSinglePrimerHeight(
              getPrimerType(this.visibilityOptions, this.model.get('layout').DNA.get('type')),
              getSliceHasAnyPrimerMods(
                this.model.get('sequence'),
                this.slice.start(),
                this.slice.end(),
                getPrimerBasesVisible(this.visibilityOptions)
              ),
              getPrimerBasesVisible(this.visibilityOptions)
            ),
          getLayersDict: (sliceModels, layeringMode) => getLayersDict(sliceModels, layeringMode),
          getSliceModels: () =>
            getSlicePrimers(
              this.model.get('sequence'),
              this.slice.start(),
              this.slice.end(),
              getPrimerBasesVisible(this.visibilityOptions),
              -1
            ),
          renderReact: (
            layout: AllPolymerLayouts['primers'],
            redraw: () => void,
            sliceModels: Array<SequencePrimer>,
            layersDict: LayersDict,
            layeringMode: LayeringMode,
            yFn: ScaleLinear<number, number>
          ) => (
            <PrimersView
              xFn={this.slice.x()}
              yFn={yFn}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequence={this.model.get('sequence')}
              contextMenuOptions={selectioncontextmenu.PRIMER_MENU}
              polymerLayout={layout}
              primerBasesVisible={getPrimerBasesVisible(this.visibilityOptions)}
              sliceHasAnyMods={getSliceHasAnyPrimerMods(
                this.model.get('sequence'),
                this.slice.start(),
                this.slice.end(),
                getPrimerBasesVisible(this.visibilityOptions)
              )}
              primerType={getPrimerType(this.visibilityOptions, this.model.get('layout').DNA.get('type'))}
              residuesType={this.model.get('layout').DNA.get('type')}
              mapType={this.mapType}
              redraw={redraw}
              slicePrimers={sliceModels}
              layersDict={layersDict}
              layeringMode={layeringMode}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.reversePrimers,
        };
        return new LayeredReactWrapperView(reversePrimersOptions);
      },
      orfs(this: SliceView): ORFsView {
        return new ORFsView({
          model: this.model,
          // @ts-expect-error
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          name: SliceComponentName.orfs,
        });
      },
      sequenceAxis(this: SliceView): SequenceAxisView {
        return new SequenceAxisView({
          model: this.model,
          // @ts-expect-error oh my god everything uses a tabstate where is this defined
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.sequenceAxis,
        });
      },
      homologyRegionAxis(this: SliceView): HomologyRegionAxisView {
        return new HomologyRegionAxisView({
          model: this.model,
          // @ts-expect-error
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.homologyRegionAxis,
        });
      },
      trace(this: SliceView): TraceView {
        return new TraceView({
          model: this.model,
          // @ts-expect-error TODO loosen model requirements
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          data: options.traceData,
          name: SliceComponentName.trace,
        });
      },
      votes(this: SliceView): ReactWrapperView<'votes'> {
        const votesOptions: ReactWrapperViewOptions<'votes'> = {
          className: 'votes',
          visibilityOptions: this.visibilityOptions,
          setting: vo.VOTES.name,
          model: this.model,
          boxHeight: ({svgHeight}) => svgHeight,
          renderReact: (
            layout: AllPolymerLayouts['votes'],
            redraw: (newBoxHeight?: number | ((layout: AllPolymerLayouts['votes']) => number)) => void
          ) => (
            <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={true}>
              <Votes
                votes={options.voteData!.get('votes')}
                layout={layout}
                rangeModel={this.range!}
                xFn={this.slice.x()}
                sequence={this.model.get('sequence')}
              />
            </ComponentWrapper>
          ),
          polymerLayoutName: 'votes',
          additionalListeners: [
            {other: options.voteData!, event: 'change:votes'},
            // Throttle update listener to avoid frequent renders during horizontal scrolling.
            {other: this.range!, event: 'rangeChange', throttleWait: 100},
          ],
          tabstate: this.tabstate,
          name: SliceComponentName.votes,
        };
        return new ReactWrapperView(votesOptions);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      line(this: SliceView): LineView<any> {
        return new LineView({
          // Protein alignments specify the row as data while other slice views specify a mapstate model
          // @ts-expect-error: Backbone expects model to always be a Backbone.Model instance
          model: this.tabstate instanceof ProteinAlignmentTabState ? this.data!.row : this.model,
          // Protein alignments allow toggling mismatches while sequence alignment have line toggle with DNA
          setting: this.tabstate instanceof ProteinAlignmentTabState ? vo.LINE.name : vo.DNA.name,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          lineLayout: this.model.get('layout').Line,
          slice: this.slice,
          behind: true,
          name: SliceComponentName.line,
        });
      },
      /**
       * Handles highlighting regions on the sequence map for multiple kinds of "link" features.
       */
      sequenceBiointelligentLinkHighlights(
        this: SliceView
      ): ReactWrapperView<'sequenceBiointelligentLinkHighlights'> {
        const biointelligentLinkOptions: ReactWrapperViewOptions<'sequenceBiointelligentLinkHighlights'> = {
          className: 'sequenceBiointelligentLinkHighlights',
          polymerLayoutName: 'sequenceBiointelligentLinkHighlights',
          // box height is decided at render and set via redraw()
          boxHeight: () => 0,
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          secondarySettings: [vo.COMPLEMENT.name],
          model: this.model,
          renderReact: (
            layout: AllPolymerLayouts['sequenceBiointelligentLinkHighlights'],
            redraw: (
              newBoxHeight?:
                | number
                | ((layout: AllPolymerLayouts['sequenceBiointelligentLinkHighlights']) => number)
            ) => void
          ) => {
            // Oligos do not have biointelligent links, and this view is not included in the map definitions
            // for oligos in client/constants/MapsConstants.ts. However, entry PDF export paths will pass
            // oligos into the sequence map sometimes (or at least they do in some tests like
            // test_entry_export_includes_attached_sequences_in_pdf_for_correct_configs),
            // so we need to guard against it.
            if (this.model.get('sequence').type() === SequenceType.OLIGO) {
              return <></>;
            }
            return (
              <BiointelligentLinkHighlightView
                xFn={this.slice.x()}
                sliceStart={this.slice.start()}
                sliceEnd={this.slice.end()}
                polymer={this.model.get('sequence')}
                polymerLayout={layout}
                residueLayoutHeight={this.model.get('layout').DNA.height()}
                residueLayoutSpacer={this.model.get('layout').DNA.get('spacer')}
                complementVisible={
                  this.visibilityOptions.getOption(vo.COMPLEMENT.name)?.get('visible') ?? false
                }
                mapType={this.mapType}
                redraw={redraw}
                linkTypes={getVisibleBiointelligentLinkTypesForHighlights(this.model.get('sequence'))}
                hoveredLinkIndices={(this.model as SequenceLinearMapState).hoveredLinkIndices() ?? null}
              />
            );
          },
          tabstate: this.tabstate,
          name: SliceComponentName.sequenceBiointelligentLinkHighlights,
        };
        return new ReactWrapperView(biointelligentLinkOptions);
      },
      /** Makes sure we have adequate room below the sequence map for any bordering bottom lines on the
       * highlights. See the docstring on BiointelligentLinkHighlightViewSpacer for more details. */
      reverseSequenceBiointelligentLinkHighlightsSpace(
        this: SliceView
      ): ReactWrapperView<'reverseSequenceBiointelligentLinkHighlightsSpace'> {
        const biointelligentLinkOptions: ReactWrapperViewOptions<'reverseSequenceBiointelligentLinkHighlightsSpace'> =
          {
            className: 'reverseSequenceBiointelligentLinkHighlightsSpace',
            polymerLayoutName: 'reverseSequenceBiointelligentLinkHighlightsSpace',
            // box height is decided at render and set via redraw()
            boxHeight: () => 0,
            disableMinimize: false,
            visibilityOptions: this.visibilityOptions,
            secondarySettings: [vo.COMPLEMENT.name],
            model: this.model,
            renderReact: (
              layout: AllPolymerLayouts['reverseSequenceBiointelligentLinkHighlightsSpace'],
              redraw: (
                newBoxHeight?:
                  | number
                  | ((
                      layout: AllPolymerLayouts['reverseSequenceBiointelligentLinkHighlightsSpace']
                    ) => number)
              ) => void
            ) => {
              // Oligos do not have biointelligent links, and this view is not included in the map definitions
              // for oligos in client/constants/MapsConstants.ts. However, entry PDF export paths will pass
              // oligos into the sequence map sometimes (or at least they do in some tests like
              // test_entry_export_includes_attached_sequences_in_pdf_for_correct_configs),
              // so we need to guard against it.
              if (this.model.get('sequence').type() === SequenceType.OLIGO) {
                return <></>;
              }
              return (
                <ReverseBiointelligentLinkHighlightViewSpace
                  sliceStart={this.slice.start()}
                  sliceEnd={this.slice.end()}
                  polymer={this.model.get('sequence')}
                  complementVisible={
                    this.visibilityOptions.getOption(vo.COMPLEMENT.name)?.get('visible') ?? false
                  }
                  mapType={this.mapType}
                  redraw={redraw}
                  linkTypes={getVisibleBiointelligentLinkTypesForHighlights(this.model.get('sequence'))}
                />
              );
            },
            tabstate: this.tabstate,
            name: SliceComponentName.reverseSequenceBiointelligentLinkHighlightsSpace,
          };
        return new ReactWrapperView(biointelligentLinkOptions);
      },
      partAnnotations(
        this: SliceView
      ): LayeredReactWrapperView<typeof MapsConstants.LINEAR_LAYER_FULL, Part, 'partAnnotations'> {
        const partAnnotationsOptions: LayeredReactWrapperViewOptions<
          typeof MapsConstants.LINEAR_LAYER_FULL,
          Part,
          'partAnnotations'
        > = {
          className: 'partAnnotations',
          polymerLayoutName: 'partAnnotations',
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          setting: vo.PART_ANNOTATIONS.name,
          model: this.model,
          // @ts-expect-error: this should probably be removed
          slice: this.slice,
          layeringMode: MapsConstants.LINEAR_LAYER_FULL,
          singleHeight: () => 2 * SINGLE_ANNOTATION_BORDER + SINGLE_ANNOTATION_HEIGHT,
          getLayersDict: (sliceModels, layeringMode) => getLayersDict(sliceModels, layeringMode),
          getSliceModels: () =>
            this.model.get('sequence').parts().getSequencePartsBetween(this.slice.start(), this.slice.end()),
          renderReact: (
            layout: AllPolymerLayouts['partAnnotations'],
            redraw: () => void,
            sliceModels: Array<Part>,
            layersDict: LayersDict,
            layeringMode: LayeringMode,
            yFn: ScaleLinear<number, number>
          ) => {
            const containerSequence: ComponentsSequence = this.model.get('sequence');
            return (
              <BiointelligentLinkAnnotations
                sequence={containerSequence}
                residuesType={this.model.get('layout').DNA.get('type')}
                sliceModels={sliceModels}
                linkType={
                  containerSequence.nucleotideType() === NucleotideType.DNA
                    ? BiointelligentLinkType.DNA_PART
                    : BiointelligentLinkType.RNA_PART
                }
                contextMenuOptions={selectioncontextmenu.getSelectionMenu()}
                sliceStart={this.slice.start()}
                sliceEnd={this.slice.end()}
                leftBP={this.slice.get('leftBP')}
                rightBP={this.slice.get('rightBP')}
                xFn={this.slice.x()}
                yFn={yFn}
                visibleWidth={this.slice.get('visibleWidth')!}
                mapType={this.mapType}
                layout={layout}
                redraw={redraw}
                layersDict={layersDict}
                layeringMode={layeringMode}
                setHoveredLinkIndices={(newIndices) => {
                  (this.model as SequenceLinearMapState).setHoveredLinkIndices(newIndices);
                }}
              />
            );
          },
          tabstate: this.tabstate,
          name: SliceComponentName.partAnnotations,
        };
        return new LayeredReactWrapperView(partAnnotationsOptions);
      },
      proteinPartAnnotations(
        this: SliceView
      ): LayeredReactWrapperView<
        typeof MapsConstants.LINEAR_LAYER_FULL,
        ProteinPart,
        'proteinPartAnnotations'
      > {
        const proteinPartAnnotationsOptions: LayeredReactWrapperViewOptions<
          typeof MapsConstants.LINEAR_LAYER_FULL,
          ProteinPart,
          'proteinPartAnnotations'
        > = {
          className: 'proteinPartAnnotations',
          polymerLayoutName: 'proteinPartAnnotations',
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          setting: vo.PART_ANNOTATIONS.name,
          model: this.model,
          layeringMode: MapsConstants.LINEAR_LAYER_FULL,
          singleHeight: () => SINGLE_ANNOTATION_HEIGHT + LINK_TO_PROTEIN_ANNOTATION_VMARGIN,
          getLayersDict: (sliceModels) =>
            getProteinPartAnnotationsLayersDict(sliceModels, this.model.get('sequence').length()),
          getSliceModels: () =>
            this.model
              .get('protein')
              .parts()
              .filter((proteinPart: ProteinPart) =>
                sequtil.isRangeOverlap(
                  proteinPart.start,
                  proteinPart.end,
                  this.slice.start(),
                  this.slice.end()
                )
              ),
          renderReact: (
            layout: AllPolymerLayouts['partAnnotations'],
            redraw: () => void,
            sliceModels: Array<ProteinPart>,
            layersDict: LayersDict,
            layeringMode: LayeringMode,
            yFn: ScaleLinear<number, number>
          ) => (
            <ProteinPartAnnotations
              layersDict={layersDict}
              layout={layout}
              proteinLength={this.model.get('protein').length()}
              redraw={redraw}
              sliceEnd={this.slice.end()}
              sliceParts={sliceModels}
              sliceStart={this.slice.start()}
              xFn={this.slice.x()}
              yFn={yFn}
              setSelection={(start, end, shouldExpand) => {
                if (shouldExpand) {
                  this.selection.expandRange(start, end);
                } else {
                  this.selection.setEndpoints(start, end);
                }
              }}
              openContextMenu={(e) => {
                ProteinContextMenus.openSelectionContextMenu(e, this.model.get('protein'));
              }}
              setHoveredLinkIndices={(newIndices) => {
                (this.model as ProteinLinearMapState).setHoveredLinkIndices(newIndices);
              }}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.proteinPartAnnotations,
        };
        return new LayeredReactWrapperView(proteinPartAnnotationsOptions);
      },
      transcriptionAnnotations(
        this: SliceView
      ): LayeredReactWrapperView<
        typeof MapsConstants.LINEAR_LAYER_FULL,
        TranscriptionMetadata,
        'transcriptionAnnotations'
      > {
        const transcriptionAnnotationsOptions: LayeredReactWrapperViewOptions<
          typeof MapsConstants.LINEAR_LAYER_FULL,
          TranscriptionMetadata,
          'transcriptionAnnotations'
        > = {
          className: 'transcriptionAnnotations',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          setting: vo.TRANSCRIPTIONS.name,
          polymerLayoutName: 'transcriptionAnnotations',
          layeringMode: MapsConstants.LINEAR_LAYER_FULL,
          name: SliceComponentName.transcriptionAnnotations,
          tabstate: this.tabstate,
          singleHeight: () => 2 * SINGLE_ANNOTATION_BORDER + SINGLE_ANNOTATION_HEIGHT,
          getLayersDict: (sliceModels: Array<TranscriptionMetadata>, layeringMode: LayeringMode) =>
            getTranscriptionAnnotationsLayersDict(sliceModels, this.model.get('sequence').length()),
          getSliceModels: () =>
            this.model.get('sequence').getTranscriptionsBetween(this.slice.start(), this.slice.end()),
          renderReact: (
            layout: AllPolymerLayouts['transcriptionAnnotations'],
            redraw: () => void,
            sliceModels: Array<TranscriptionMetadata>,
            layersDict: LayersDict,
            layeringMode: LayeringMode,
            yFn: ScaleLinear<number, number>
          ) => {
            const containerSequence: Sequence = this.model.get('sequence');
            return (
              <BiointelligentLinkAnnotations
                xFn={this.slice.x()}
                yFn={yFn}
                sliceStart={this.slice.start()}
                sliceEnd={this.slice.end()}
                leftBP={this.slice.get('leftBP')}
                rightBP={this.slice.get('rightBP')}
                visibleWidth={this.slice.get('visibleWidth')!}
                sequence={containerSequence}
                residuesType={this.model.get('layout').DNA.get('type')}
                contextMenuOptions={selectioncontextmenu.getSelectionMenu()}
                mapType={this.mapType}
                layout={layout}
                redraw={redraw}
                linkType={BiointelligentLinkType.TRANSCRIPTION}
                layersDict={layersDict}
                layeringMode={layeringMode}
                sliceModels={sliceModels}
                setHoveredLinkIndices={(newIndices) => {
                  (this.model as SequenceLinearMapState).setHoveredLinkIndices(newIndices);
                }}
              />
            );
          },
        };
        return new LayeredReactWrapperView(transcriptionAnnotationsOptions);
      },
      gapToRealAxis(this: SliceView): GapToRealAxisView {
        return new GapToRealAxisView({
          model: this.model,
          // @ts-expect-error
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          name: SliceComponentName.gapToRealAxis,
        });
      },
      alignmentComments(this: SliceView): AlignmentCommentsView {
        return new AlignmentCommentsView({
          model: this.model,
          // @ts-expect-error
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.alignmentComments,
        });
      },
      primerAssemblyMismatches(this: SliceView): PrimerAssemblyMismatchView {
        const primerAssemblyMismatchViewOptions: PrimerAssemblyMismatchViewOptions = {
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          name: SliceComponentName.primerAssemblyMismatches,
          tabstate: this.tabstate,
        };
        return new PrimerAssemblyMismatchView(primerAssemblyMismatchViewOptions);
      },
      goldenGateCutSites(this: SliceView): GoldenGateCutSiteView {
        const goldenGateCutSiteViewOptions: LayeredViewOptions = {
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          name: SliceComponentName.goldenGateCutSites,
          tabstate: this.tabstate,
        };
        return new GoldenGateCutSiteView(goldenGateCutSiteViewOptions);
      },
      proteinAxis(this: SliceView): ProteinAxisView {
        return new ProteinAxisView({
          model: this.model,
          // @ts-expect-error
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          name: SliceComponentName.proteinAxis,
        });
      },
      proteinAlignmentAxis(this: SliceView): ProteinAlignmentAxisView {
        return new ProteinAlignmentAxisView({
          // @ts-expect-error
          tabstate: this.tabstate,
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          range: this.range,
          parent: this,
          name: SliceComponentName.proteinAlignmentAxis,
        });
      },
      aminoAcidsSearch(this: SliceView): ReactWrapperView<'aminoAcids'> {
        const aminoAcidsSearchOptions: ReactWrapperViewOptions<'aminoAcids'> = {
          className: 'aminoAcidsSearch',
          model: this.model,
          setting: vo.SEARCH_AMINO_ACIDS.name,
          visibilityOptions: this.visibilityOptions,
          // Since this is an overlay, need to listen for changes to the aminoAcid layout
          // in case the height needs to be changed.
          polymerLayoutName: 'aminoAcids',
          disableMinimize: true,
          boxHeight: () => 0,
          renderReact: (
            layout: AllPolymerLayouts['aminoAcids'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={true}>
              <AminoAcidsSearchResults
                proteinId={this.model.polymer!().id}
                layout={layout}
                sliceStart={this.slice.start()}
                sliceEnd={this.slice.end()}
                xFn={this.slice.x()}
              />
            </ComponentWrapper>
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.aminoAcidsSearch,
        };
        return new ReactWrapperView(aminoAcidsSearchOptions);
      },
      codonOptimization(this: SliceView): ReactWrapperView<'codonOptimization'> {
        const dnaLayout = this.model.get('layout').DNA;
        const sequence = this.model.polymer!() as Sequence;
        const codonOptimizationSelectionOptions: ReactWrapperViewOptions<'codonOptimization'> = {
          className: 'codonOptimization',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'codonOptimization',
          boxHeight: () => 0,
          renderReact: (
            layout: AllPolymerLayouts['codonOptimization'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={true}>
              <CodonOptimizationSelection
                sequence={sequence}
                sliceStart={this.slice.start()}
                sliceEnd={this.slice.end()}
                xFn={this.slice.x()}
                showingComplement={dnaLayout.get('complement')}
                basesHeight={dnaLayout.height() + dnaLayout.get('spacer')}
              />
            </ComponentWrapper>
          ),
          additionalListeners: [
            ...sequence
              .fileState()
              .buffers()
              .map((buffer: BufferState) => ({
                other: buffer.tabs(),
                event: 'add remove reset change:active',
              })),
            {other: dnaLayout, event: 'change'},
          ],
          tabstate: this.tabstate,
          name: SliceComponentName.codonOptimization,
        };
        return new ReactWrapperView(codonOptimizationSelectionOptions);
      },
      proteinAnnotations(this: SliceView): ProteinAnnotationsView {
        return new ProteinAnnotationsView({
          model: this.model as ProteinLinearMapState,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.proteinAnnotations,
        });
      },
      proteinAlignmentAnnotations(this: SliceView): ProteinAlignmentAnnotationsView {
        return new ProteinAlignmentAnnotationsView({
          // @ts-expect-error TODO loosen model requirements
          model: this.model as ProteinAlignmentTabState,
          tabstate: this.tabstate,
          visibilityOptions: this.visibilityOptions,
          annotations: this.data!.row.proteinVersion.annotations,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
          name: SliceComponentName.proteinAlignmentAnnotations,
        });
      },
      proteinPIGraph(this: SliceView): ReactWrapperView<SliceComponentName.proteinPIGraph> {
        return this._proteinBarGraphWrapperView({
          getValues: () => {
            const aminoAcids = this.getResidueSlice();
            return Array.from(aminoAcids).map((aminoAcid) =>
              ProteinProperties.getRelativeIsoelectricPoint(aminoAcid)
            );
          },
          propertyName: 'pI',
          componentName: SliceComponentName.proteinPIGraph,
          graphClassName: 'pIBarGraph',
          setting: vo.PROTEIN_PI_GRAPH.name,
        });
      },
      proteinHydrophobicityGraph(
        this: SliceView
      ): ReactWrapperView<SliceComponentName.proteinHydrophobicityGraph> {
        return this._proteinBarGraphWrapperView({
          getValues: () => {
            const aminoAcids = this.getResidueSlice();
            return Array.from(aminoAcids).map((aminoAcid) =>
              ProteinProperties.getRelativeHydrophobicity(aminoAcid)
            );
          },
          propertyName: 'Hydrophobicity',
          componentName: SliceComponentName.proteinHydrophobicityGraph,
          graphClassName: 'hydrophobicityBarGraph',
          setting: vo.PROTEIN_HYDROPHOBICITY_GRAPH.name,
        });
      },
      proteinAlignmentIdentityGraph(
        this: SliceView
      ): ReactWrapperView<SliceComponentName.proteinAlignmentIdentityGraph> {
        return this._proteinBarGraphWrapperView({
          getValues: () => {
            const alignment = this.tabstate.get('alignment');
            const aminoAcidMatrix = getAminoAcidMatrix(alignment.rows, this.slice.start(), this.slice.end());
            return getIdentityValues(aminoAcidMatrix);
          },
          propertyName: 'Identity',
          componentName: SliceComponentName.proteinAlignmentIdentityGraph,
          graphClassName: 'alignmentIdentityGraph',
          setting: vo.PROTEIN_ALIGNMENT_IDENTITY_GRAPH.name,
        });
      },
      antibodyNumbering(this: SliceView): ReactWrapperView<'antibodyNumbering'> {
        const antibodyNumberingOptions: ReactWrapperViewOptions<'antibodyNumbering'> = {
          className: 'antibodyNumbering',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          setting: vo.ANTIBODY_NUMBERING.name,
          polymerLayoutName: 'antibodyNumbering',
          disableMinimize: false,
          boxHeight: () => 0,
          renderReact: (
            layout: MapsConstants.AntibodyNumberingLayout,
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <AntibodyNumberingSlice
              layout={layout}
              // @ts-expect-error TS doesn't like checking function properties without invoking
              onAlignment={!this.model.protein}
              redraw={redraw}
              // On a protein linear map, this.model is a map state with a protein getter
              // On a protein alignment, this.model is a tab state
              residues={
                // @ts-expect-error TS doesn't like checking function properties without invoking
                this.model.protein
                  ? (this.model as ProteinLinearMapState).protein().aminoAcids()
                  : this._polymerResidues!
              }
              scheme={this.visibilityOptions
                .getOption(vo.ANTIBODY_NUMBERING.name)!
                .getSetting('numberingScheme')!
                .get('value')}
              setSelection={this.selection.setEndpoints.bind(this.selection)}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              xFn={this.slice.x()}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.antibodyNumbering,
        };
        return new ReactWrapperView(antibodyNumberingOptions);
      },
      proteinParts(this: SliceView): ReactWrapperView<'proteinParts'> {
        const partsOptions: ReactWrapperViewOptions<'proteinParts'> = {
          className: 'proteinParts',
          polymerLayoutName: 'proteinParts',
          // box height is decided at render and set via redraw()
          boxHeight: () => 0,
          disableMinimize: false,
          visibilityOptions: this.visibilityOptions,
          // Note that the `vo.PARTS` option cannot actually be turned off in the UI.
          // When users toggle parts visibility, it only affects the "part annotations".
          // This is so that users always have an indication of which sections are editable/have dependencies.
          setting: vo.PARTS.name,
          model: this.model,
          renderReact: (
            layout: AllPolymerLayouts['proteinParts'],
            redraw: (newBoxHeight?: number | ((layout: AllPolymerLayouts['proteinParts']) => number)) => void
          ) => (
            <BiointelligentLinkHighlightView
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              polymer={(this.model as ProteinLinearMapState).protein()}
              polymerLayout={layout}
              residueLayoutHeight={this.model.get('layout').AminoAcids.height()}
              residueLayoutSpacer={this.model.get('layout').AminoAcids.get('spacer')}
              complementVisible={false}
              mapType={this.mapType}
              redraw={redraw}
              linkTypes={[BiointelligentLinkType.AMINO_ACID_PART]}
              hoveredLinkIndices={(this.model as ProteinLinearMapState).hoveredLinkIndices() ?? null}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.proteinParts,
        };
        return new ReactWrapperView(partsOptions);
      },
      modifiedNucleotides(this: SliceView): ReactWrapperView<'modifiedNucleotides'> {
        const modifiedNucleotidesOptions: ReactWrapperViewOptions<'modifiedNucleotides'> = {
          className: 'modifiedNucleotides',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          secondarySettings: [vo.NUCLEOTIDE_STRUCTURE_IMAGES.name],
          polymerLayoutName: 'modifiedNucleotides',
          // Give modifiedNucleotides component extra space at top of each slice.
          boxHeight: (layout: AllPolymerLayouts['modifiedNucleotides']) =>
            layout.visible ? MODIFIED_NUCLEOTIDES_BOX_HEIGHT : 0,
          disableMinimize: false,
          shouldUpdateForAdjust: true,
          renderReact: (
            layout: AllPolymerLayouts['modifiedNucleotides'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <ModifiedNucleotides
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequenceId={this.model.get('sequence').get('id')}
              bases={this.getResidueSlice()}
              showNucleotideStructures={this.visibilityOptions
                .getOption(vo.NUCLEOTIDE_STRUCTURE_IMAGES.name)!
                .get('visible')}
              layout={layout}
              boxHeightToDNA={this.boxHeightToDNA(SliceComponentName.modifiedNucleotides)}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.modifiedNucleotides,
        };
        return new ReactWrapperView(modifiedNucleotidesOptions);
      },
      modifiedNucleotideSummary(this: SliceView): ReactWrapperView<'modifiedNucleotideSummary'> {
        const modifiedNucleotidesSummaryOptions: ReactWrapperViewOptions<'modifiedNucleotideSummary'> = {
          className: 'modifiedNucleotideSummary',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'modifiedNucleotideSummary',
          boxHeight: () => 0,
          disableMinimize: false,
          renderReact: (
            layout: AllPolymerLayouts['modifiedNucleotideSummary'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <ModifiedNucleotideSummary
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequenceId={this.model.get('sequence').get('id')}
              layout={layout}
              redraw={redraw}
              sliceBases={this.getResidueSlice()}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.modifiedNucleotideSummary,
        };
        return new ReactWrapperView(modifiedNucleotidesSummaryOptions);
      },
      forwardTranscriptionLine(this: SliceView): ReactWrapperView<'forwardTranscriptionLine'> {
        const forwardTranscriptionLineOptions: ReactWrapperViewOptions<'forwardTranscriptionLine'> = {
          className: 'forwardTranscriptionLine',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'forwardTranscriptionLine',
          setting: vo.TRANSCRIPTIONS.name,
          // box height is decided at render and set via redraw()
          boxHeight: () => 0,
          disableMinimize: false,
          renderReact: (
            layout: AllPolymerLayouts['forwardTranscriptionLine'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <TranscriptionLine
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequence={this.model.get('sequence')}
              layout={layout}
              isReverseComplement={false}
              redraw={redraw}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.forwardTranscriptionLine,
        };
        return new ReactWrapperView(forwardTranscriptionLineOptions);
      },
      reverseTranscriptionLine(this: SliceView): ReactWrapperView<'reverseTranscriptionLine'> {
        const reverseTranscriptionLineOptions: ReactWrapperViewOptions<'reverseTranscriptionLine'> = {
          className: 'reverseTranscriptionLine',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'reverseTranscriptionLine',
          setting: vo.TRANSCRIPTIONS.name,
          // box height is decided at render and set via redraw()
          boxHeight: () => 0,
          disableMinimize: false,
          renderReact: (
            layout: AllPolymerLayouts['reverseTranscriptionLine'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <TranscriptionLine
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequence={this.model.get('sequence')}
              layout={layout}
              isReverseComplement={true}
              redraw={redraw}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.reverseTranscriptionLine,
        };
        return new ReactWrapperView(reverseTranscriptionLineOptions);
      },
      transcriptionArrow(this: SliceView): ReactWrapperView<'transcriptionArrow'> {
        const transcriptionArrowOptions: ReactWrapperViewOptions<'transcriptionArrow'> = {
          className: 'transcriptionArrow',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'transcriptionArrow',
          setting: vo.TRANSCRIPTIONS.name,
          // box height is decided at render and set via redraw()
          boxHeight: () => 0,
          disableMinimize: false,
          secondarySettings: [
            // All toggleable slices between transcription arrow and forward or reverse lines on the
            // linear map should be listed here. This insures that transcription connector line height is
            // rendered correctly when the intermittent slices are hidden or displayed
            vo.COMPLEMENT.name,
            vo.PARTS.name,
            vo.PART_ANNOTATIONS.name,
            vo.PRIMERS.name,
            vo.PRIMER_BASES.name,
          ],
          renderReact: (
            layout: AllPolymerLayouts['transcriptionArrow'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <TranscriptionArrow
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequence={this.model.get('sequence')}
              layout={layout}
              redraw={redraw}
              boxHeightToTranscriptionLine={(reverseComplement: boolean) => {
                const transcriptionLineLayer = reverseComplement
                  ? SliceComponentName.reverseTranscriptionLine
                  : SliceComponentName.forwardTranscriptionLine;
                return this.boxHeightBetweenLayers(
                  SliceComponentName.transcriptionArrow,
                  transcriptionLineLayer
                );
              }}
              setSelection={(start, end) => {
                this.selection.setEndpoints(start, end);
              }}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.transcriptionArrow,
        };
        return new ReactWrapperView(transcriptionArrowOptions);
      },
      nucleotidesAxisOverlay(this: SliceView): ReactWrapperView<'nucleotidesAxisOverlay'> {
        const nucleotidesAxisOverlayOptions: ReactWrapperViewOptions<'nucleotidesAxisOverlay'> = {
          className: 'nucleotidesAxisOverlay',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'nucleotidesAxisOverlay',
          boxHeight: () => 0,
          disableMinimize: false,
          renderReact: (
            layout: AllPolymerLayouts['nucleotidesAxisOverlay'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <NucleotidesAxisOverlay
              xFn={this.slice.x()}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              sequenceId={this.model.get('sequence').get('id')}
              layout={layout}
              bases={this.getResidueSlice()}
              nucleotideType={this.model.get('sequence').nucleotideType()}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.nucleotidesAxisOverlay,
        };
        return new ReactWrapperView(nucleotidesAxisOverlayOptions);
      },
      nucleotidesPositionHighlight(this: SliceView): ReactWrapperView<'nucleotidesPositionHighlight'> {
        const nucleotidesPositionHighlightOptions: ReactWrapperViewOptions<'nucleotidesPositionHighlight'> = {
          className: 'nucleotidesPositionHighlight',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          polymerLayoutName: 'nucleotidesPositionHighlight',
          boxHeight: () => 0,
          disableMinimize: false,
          renderReact: (
            layout: AllPolymerLayouts['nucleotidesPositionHighlight'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <NucleotidePositionHighlight
              height={this.distanceFromSliceTopToBelowDNAResidues()}
              xFn={this.slice.x()}
              sequenceId={this.model.get('sequence').get('id')}
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              layout={layout}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.nucleotidesPositionHighlight,
        };
        return new ReactWrapperView(nucleotidesPositionHighlightOptions);
      },
      proteinLiabilitySites(this: SliceView): ReactWrapperView<'proteinLiabilitySites'> {
        const defaultLayout = this.model.get('polymerLayout').proteinLiabilitySites;
        const boxHeightForPNG = defaultLayout.height + defaultLayout.vMargin;

        const proteinLiabilitySitesOptions: ReactWrapperViewOptions<'proteinLiabilitySites'> = {
          className: 'proteinLiabilitySites',
          model: this.model,
          visibilityOptions: this.visibilityOptions,
          setting: vo.PROTEIN_LIABILITY_SITES.name,
          secondarySettings: [vo.ANTIBODY_CDR_ANNOTATIONS.name],
          polymerLayoutName: 'proteinLiabilitySites',
          disableMinimize: false,
          boxHeight: () => 0,
          _staticBoxHeight: this._forAlignmentPNGExport ? boxHeightForPNG : undefined,
          renderReact: (
            layout: AllPolymerLayouts['proteinLiabilitySites'],
            redraw: (newBoxHeight?: number | (() => number)) => void
          ) => (
            <ProteinLiabilitySites
              layout={layout}
              redraw={redraw}
              residues={
                // @ts-expect-error TS doesn't like checking function properties without invoking
                this.model.protein
                  ? (this.model as ProteinLinearMapState).protein().aminoAcids()
                  : this._polymerResidues!
              }
              sliceStart={this.slice.start()}
              sliceEnd={this.slice.end()}
              visibilityOptions={this.visibilityOptions}
              xFn={this.slice.x()}
            />
          ),
          tabstate: this.tabstate,
          name: SliceComponentName.proteinLiabilitySites,
        };
        return new ReactWrapperView(proteinLiabilitySitesOptions);
      },
      selectionHiddenWrapper(this: SliceView) {
        const selectionHiddenWrapperViewOptions: ComponentOptions = {
          model: this.model,
          name: SliceComponentName.selectionHiddenWrapper,
          tabstate: this.tabstate,
        };
        return new SelectionHiddenWrapperView(selectionHiddenWrapperViewOptions);
      },
    };

    this.components = {};
    this.zComponents = [];
    this.rComponents = [];
    for (const name of this.y) {
      this.components[name] = componentMethods[name].call(this);
    }

    for (const name of this.z) {
      this.zComponents.push(this.components[name]!);
    }

    for (const name of r) {
      this.rComponents.push(this.components[name]!);
    }

    $(document).on(`mouseup.slice-${this.slice.cid}`, () => {
      this.selection.set('active', null);
      if (this.trim != null) {
        this.trim.stopDrag();
      }
    });
  }

  registerListeners(): void {
    this.stopListening();
    this.listenTo(
      this.slice,
      'change:width',
      (model: unknown, value: unknown, {skipUpdate}: {skipUpdate?: boolean} = {}) => {
        // Sequence map and linear map will explicitly call update after setting slice attributes,
        // causing a double render if we were to call @update() here. Alignments however currently
        // rely on this to trigger re-renders, so skipping the update is opt-in.
        if (!skipUpdate) {
          this.update();
        }
      }
    );
    this.listenTo(this.model, 'change:hoveredLinkIndices', () => {
      this.update();
    });
    for (const name of Object.keys(this.components)) {
      const component = this.components[name as SliceComponentName];
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(component, 'redraw', this.adjustAndSelect);
    }

    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.selection, 'change', this.doSelect);
    if (this.trim != null) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(this.trim, 'change', this.doTrim);
    }
    this.listenTo(this.visibilityOptions.getOption(MAXIMISE.name), 'change:visible', () => {
      this.update();
    });
  }

  getMinSliceHeight(): number {
    return this.visibilityOptions.getOption(MAXIMISE.name)?.get('visible')
      ? EXPANDED_MIN_SLICE_HEIGHT
      : DEFAULT_MIN_SLICE_HEIGHT;
  }

  getSlice(): Slice {
    return this.slice;
  }

  contextMenuData(): MenuChoicesOptions {
    const maybeSequence = this.model.polymer != null && this.model.polymer();
    // eslint-disable-next-line no-restricted-syntax
    return _.extend(
      {
        model: this.model,
        circular: maybeSequence instanceof Sequence ? maybeSequence.circular() : false,
        hasTrim: this.trim != null,
      },
      this.selectionOptions
    );
  }

  _proteinBarGraphWrapperView<
    T extends
      | SliceComponentName.proteinPIGraph
      | SliceComponentName.proteinHydrophobicityGraph
      | SliceComponentName.proteinAlignmentIdentityGraph,
  >({
    getValues,
    propertyName,
    componentName,
    graphClassName,
    setting,
  }: {
    getValues: () => Array<number | null>;
    propertyName: 'pI' | 'Hydrophobicity' | 'Identity';
    componentName: T;
    graphClassName: 'pIBarGraph' | 'hydrophobicityBarGraph' | 'alignmentIdentityGraph';
    setting: string;
  }): ReactWrapperView<T> {
    const proteinBarGraphWrapperViewOptions: ReactWrapperViewOptions<T> = {
      className: componentName,
      setting,
      model: this.model,
      visibilityOptions: this.visibilityOptions,
      polymerLayoutName: componentName,
      boxHeight: (layout: AllPolymerLayouts[T]) => layout.height + layout.vMargin,
      renderReact: (layout: AllPolymerLayouts[T]) => {
        const values = getValues();
        const xFn = (i: number): number => this.slice.get('x')!(i + this.slice.start());
        return (
          <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
            <TooltipBarGraph
              values={values}
              maxValue={1}
              getFormattedTooltipTitle={(value: number | null) =>
                value != null ? `${propertyName}: ${value.toFixed(2)}` : null
              }
              xFn={xFn}
              maxHeight={layout.height}
              baselineProps={{stroke: 'black'}}
              barRectProps={{className: graphClassName}}
            />
          </ComponentWrapper>
        );
      },
      tabstate: this.tabstate,
      name: componentName,
    };
    return new ReactWrapperView(proteinBarGraphWrapperViewOptions);
  }

  generateRectangles(sStart: number, sEnd: number | null): Array<Rectangle | Array<Rectangle>> {
    // Callsite flattens into more coherent data type
    const results: Array<Rectangle | Array<Rectangle>> = [];
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (sEnd === null) {
      return results;
    }
    const start = this.slice.start();
    const end = this.slice.end();

    if (sStart < sEnd) {
      // no wraps = normal
      results.push([{start: sStart, end: sEnd}]);
    } else if (start <= sEnd && sEnd <= sStart && sStart < end) {
      // if we have a bad wrap, add the bad boy.
      results.push({start: start - 1, end: sEnd});
      results.push({start: sStart, end: end + 1});
    } else {
      const left = start <= sStart && sStart < end ? sStart : start - 1;
      const right = start <= sEnd && sEnd < end ? sEnd : end + 1;
      results.push({start: left, end: right});
    }
    return results;
  }

  killSelection(): void {
    d3.select(this.el).selectAll('.selection-component').remove();
  }

  createSelectRect(
    root: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>,
    x: (n: number) => number
  ): void {
    const axisLast = AXIS_CLASSES.includes(this.y[this.y.length - 1]);

    function iterator(memo: number, view: {boxHeight(): number}, className: SliceComponentName): number {
      if (AXIS_CLASSES.includes(className) && axisLast) {
        return memo;
      } else {
        return memo + view.boxHeight();
      }
    }

    // eslint-disable-next-line no-restricted-syntax
    const height = Math.max(_.reduce(this.components, iterator, 0), this.getMinSliceHeight());

    interface Datum {
      start: number;
      end: number;
    }

    root
      .attr('x', (d: Datum) => x(d.start))
      .attr('y', 0)
      .attr('width', (d: Datum) => x(d.end) - x(d.start))
      .attr('height', (d: Datum) => height);
  }

  checkRectsEqual(oldRects: Array<Rect>, newRects: Array<Rect>): boolean {
    if (oldRects.length !== newRects.length) {
      return false;
    } else {
      for (let index = 0; index < oldRects.length; index++) {
        const oldRect = oldRects[index];
        const newRect = newRects[index];
        if (oldRect.start !== newRect.start || oldRect.end !== newRect.end) {
          return false;
        }
      }
      return true;
    }
  }

  generateTrimRectangles(tStart: number, tEnd: number): Array<Rectangle> {
    const ret = [];
    if (this.slice.start() < tStart) {
      ret.push({start: this.slice.start(), end: tStart});
    }
    if (this.slice.end() > tEnd) {
      ret.push({start: tEnd, end: this.slice.end()});
    }
    return ret;
  }

  renderTrim(): void {
    const data = this.generateTrimRectangles(this.trim!.start(), this.trim!.end());
    const visible = d3.select(this.el).selectAll('rect.trim').data(data);
    visible.enter().insert('svg:rect', ':first-child').classed('trim', true);
    visible.exit().remove();
    this.createSelectRect(visible, this.slice.x());
  }

  renderSelect(): void {
    const data: Array<Rectangle> = _.chain(this.selection.slice(this.slice.start(), this.slice.end()))
      .map((selection) => this.generateRectangles(selection.start(), selection.end()))
      .flatten()
      .value() as Array<Rectangle>; // Underscore types don't account for flattening properly

    // If we initiate a selection on a hidden rectangle, the hidden rectangle gets removed
    // as the selection is reset. This prevents drag scrolling from working in FF, so we
    // leave 0-width hidden rectangles.
    while (data.length < 2) {
      data.push({start: this.slice.start(), end: this.slice.start()});
    }

    const boxHeight = this.boxHeight();

    // Optimisation: only re-render selection if the selection has changed.
    if (this.oldRectangle.length === 0 && data.length === 0) {
      return;
    }
    if (
      this.checkRectsEqual(this.oldRectangle, data) &&
      this.oldBoxHeight === boxHeight &&
      this.oldSelectStart === this.slice.start() &&
      this.oldSelectEnd === this.slice.end() &&
      this.oldWidth === this.slice.get('width') &&
      this.selection.canPCR() === this.oldCanPCR
    ) {
      return;
    }
    this.oldRectangle = data;
    this.oldSelectStart = this.slice.start();
    this.oldSelectEnd = this.slice.end();
    this.oldCanPCR = this.selection.canPCR();
    this.oldWidth = this.slice.get('width')!;

    const x = this.slice.x();

    // Update visible selection rectangle
    let visible = d3.select(this.el).selectAll('rect.selection').data(data);
    visible = visible
      .enter()
      .insert('svg:rect', ':first-child')
      .merge(visible)
      .classed('selection', true)
      .classed('selection-component', true)
      .classed('can-pcr', this.selection.canPCR())
      .classed('cant-edit', !this.canEditSelection());
    this.createSelectRect(visible, x);

    // Update hidden right-click target rectangle
    const hidden = d3.select(this.el).selectAll('g.selection-hidden-wrapper').data([data]);
    addTestAttrsToD3Selection(hidden, {component: 'SliceView', element: 'selection'});
    let hiddenRects = hidden.selectAll('rect.selection-hidden').data((d) => d);
    hiddenRects = hiddenRects
      .join('svg:rect')
      .classed('selection-hidden', true)
      .classed('selection-component', true)
      .on('contextmenu', (e, d) => {
        this.openSelectionContextMenu(e);
        trackSequenceMapRightClickWithSelectedBases(this.model.get('sequence'));
      });
    this.createSelectRect(hiddenRects, x);
  }

  die(): void {
    this.stopListening();
    $(document).off(`.slice-${this.slice.cid}`);
    // eslint-disable-next-line no-restricted-syntax
    _.each(this.components, (view, className, list) => {
      view.die();
    });
  }

  getTop(): number {
    const $parent = this.$el.parents('.buffer-tab');
    const parentOffset = $parent.offset()!.top;
    const parentScrollTop = $parent[0].scrollTop;
    return parentScrollTop + this.el.getBoundingClientRect().top - parentOffset - 10;
  }

  updateSelectionFromScroll(offsetX: number): void {
    const offset = offsetX - this.$el.offset()!.left;
    const x = Math.round(this.slice.xInv()(offset));

    if (this.trim != null && this.trim.isActive()) {
      this.trim.drag(x);
    } else if (this.selection.get('active')) {
      this.selection.linearExtend(x);
    }
  }

  updateSelection(event: JQueryEventObject | 'contextmenu'): void {
    // @ts-expect-error event shenanigans
    if ((event.which === 3 || event.ctrlKey || event === 'contextmenu') && event.altKey && window.debug) {
      return;
    }
    const offset = (event as JQueryEventObject).pageX - this.$el.offset()!.left;
    // FIXME: Why do we need to mod? When resizing end to make smaller selection, x > length???
    const x = clamp(0, this.getPolymerLength(), Math.round(this.slice.xInv()(offset)));
    if (this.trim != null) {
      switch ((event as JQueryEventObject).type) {
        case 'mousemove':
          if (this.trim.isActive()) {
            this.trim.drag(x);
          }
          break;
        case 'mouseup':
          this.trim.stopDrag();
          break;
        default:
          break;
      }
    }
    // for selections
    switch ((event as JQueryEventObject).type) {
      case 'mousemove':
      case 'mousewheel':
        if (this.selection.get('active') === this.mapType) {
          this.selection.linearExtend(x);
        }
        break;
      case 'mousedown':
        _.defer(() => {
          if (this.focusableParent) {
            this.focusableParent.focus();
          }
        });
        window.getSelection()!.removeAllRanges(); // prevents dragging a selection in FF
        trackSequenceMapClick(this.model.get('sequence'), this.mapType);
        if ((event as JQueryEventObject).shiftKey) {
          context.using({isLinear: true}, this, () => {
            this.selection.expand(x);
            if (this.selection.end() != null) {
              trackSequenceMapBaseSelection(
                this.model.get('sequence'),
                BaseSelectionMethod.shiftClickDown,
                this.selection.start(),
                this.selection.end(),
                this.mapType
              );
            }
          });
          this.selection.set({active: this.mapType});
          (event as JQueryEventObject).stopPropagation();
        } else if ((event as JQueryEventObject).which !== 3 && !(event as JQueryEventObject).ctrlKey) {
          this.selection.reset(x);
          this.selection.set({active: this.mapType});
          (event as JQueryEventObject).stopPropagation();
        }
        break;
      case 'contextmenu': {
        this.selection.reset(x);
        this.selection.set({active: this.mapType});
        break;
      }
      case 'mouseup':
        // If we were doing something in the first place, we should handle it, otherwise let it go.
        if (this.selection.get('active') === this.mapType) {
          this.selection.set({active: null});
          if ((event as JQueryEventObject).which !== 3 && !(event as JQueryEventObject).ctrlKey) {
            this.selection.linearExtend(x);
            const selectionMethod = (event as JQueryEventObject).shiftKey
              ? BaseSelectionMethod.shiftClickUp
              : BaseSelectionMethod.clickAndDrag;
            trackSequenceMapBaseSelection(
              this.model.get('sequence'),
              selectionMethod,
              this.selection.start(),
              this.selection.end(),
              this.mapType
            );
          }
        }
        break;
      case 'dblclick': {
        this.handleDoubleClick(event);
        trackSequenceMapBaseSelection(
          this.model.get('sequence'),
          BaseSelectionMethod.doubleClick,
          this.selection.start(),
          this.selection.end(),
          this.mapType
        );
        (event as JQueryEventObject).stopPropagation();
        break;
      }
      default:
        break;
    }
  }

  doSelect(): void {
    this.renderSelect();
    this.selectLines();
  }

  doTrim(): void {
    this.renderTrim();
    this.trimLines();
  }

  checkLinesEqual(oldLines: Array<SelectLinesDatum>, newLines: Array<SelectLinesDatum>): boolean {
    if (oldLines.length !== newLines.length) {
      return false;
    } else {
      for (let index = 0; index < oldLines.length; index++) {
        const oldLine = oldLines[index];
        const newLine = newLines[index];
        if (oldLine.type !== newLine.type || oldLine.x !== newLine.x) {
          return false;
        }
      }
      return true;
    }
  }

  trimLines(): void {
    const data = [];
    if (this.trim!.start() === this.slice.end() || this.slice.contains(this.trim!.start())) {
      data.push({type: 'start', x: this.trim!.start()});
    }
    if (this.trim!.end() === 0 || this.slice.contains(this.trim!.end() - 1)) {
      data.push({type: 'end', x: this.trim!.end()});
    }

    if (this.trim!.start() === this.trim!.end() && this.trim!.end() === this.slice.end()) {
      data.reverse();
    }

    let root = d3.select(this.el).selectAll('.trim-line').data(data);
    const enter = root.enter().insert('svg:path');
    enter.append('svg:title').text('Trim');
    root = root.merge(enter);
    const boxHeight = this.boxHeight();
    const x = this.slice.x();
    const {trim} = this;
    root
      .classed('trim-line', true)
      .attr('d', (d) => `M${x(d.x) + (d.type === 'start' ? 10 : -10)},1L${x(d.x)},1L${x(d.x)},${boxHeight}`)
      .on('mousedown', (e: Event, d: SelectLinesDatum) => {
        e.stopPropagation();
        trim!.startDrag(d.type);
        analytics.track('editor', 'trimmed_sequence');
      });
    root.exit().remove();
    addTestAttrsToD3Selection(root, {component: 'SliceView', element: 'trimLine'});
  }

  selectLines(): void {
    const x = this.slice.x();
    const selection = this.selection;
    const selections = selection.slice(this.slice.start(), this.slice.end());

    const data: Array<SelectLinesDatum> = [];

    if (this.slice.contains(selection.start())) {
      data.push({type: 'start', x: selection.start()});
    }

    const polymerLength = this.getPolymerLength();
    const selectionEnd = selection.end();
    if (
      selectionEnd != null &&
      (this.slice.contains(selectionEnd - 1) ||
        selectionEnd === this.slice.end() ||
        (this.slice.end() === polymerLength && selection.start() === polymerLength))
    ) {
      // selection must be non-empty in this case since the end equals the non-null slice end.
      data.push({type: 'end', x: (selection as NonEmptySelection).end()});
    }

    const boxHeight = this.boxHeight();
    if (this.oldLineData.length === 0 && data.length === 0) {
      return;
    }
    if (
      this.checkLinesEqual(this.oldLineData, data) &&
      this.oldBoxHeight === boxHeight &&
      this.oldLineStart === this.slice.start() &&
      this.oldLineEnd === this.slice.end() &&
      this.oldWidthLines === this.slice.get('width') &&
      this.oldEmpty === selection.isEmpty()
    ) {
      return;
    }

    this.oldLineStart = this.slice.start();
    this.oldLineEnd = this.slice.end();
    this.oldWidthLines = this.slice.get('width')!;
    this.oldLineData = data;
    this.oldEmpty = selection.isEmpty();
    const g = d3.select(this.el);
    let hide = selections.length === 0 || !selection.isEmpty();
    let root = g.selectAll('.map-cursor-line');
    root.classed('hide', hide);
    root = root.data(selections);
    root = root
      .enter()
      .append('svg:line')
      .merge(root)
      .classed('map-cursor-line', true)
      .classed('selection-component', true)
      .attr('x1', (d: Selection) => x(d.start()))
      .attr('y1', 0)
      .attr('x2', (d: Selection) => x(d.start()))
      .attr('y2', boxHeight)
      .classed('hide', hide)
      .classed('line-blink', false);

    addTestAttrsToD3Selection(root, {component: 'SliceView', element: 'mapCursorLine'});
    if (!hide) {
      setTimeout(() => {
        this.blinkCursor();
      }, 100);
    }

    const {mapType} = this;

    hide = selection.isEmpty();
    root = g.selectAll('.selection-line').data(data);
    root
      .enter()
      .insert('svg:line')
      .merge(root)
      .classed('selection-line', true)
      .classed('selection-component', true)
      .attr('x1', (d: SelectLinesDatum) => x(d.x))
      .attr('y1', 0)
      .attr('x2', (d: SelectLinesDatum) => x(d.x))
      .attr('y2', boxHeight)
      .classed('hide', hide)
      .on('mousedown', (e: Event, d: SelectLinesDatum) => {
        e.stopPropagation();
        selection.setFreeEnd(d.type);
        selection.set('active', mapType);
      });
    root.exit().remove();

    root = g.selectAll('.cursor-triangle').data(data);
    root
      .enter()
      .insert('svg:polygon')
      .merge(root)
      .classed('cursor-triangle', true)
      .classed('selection-component', true)
      .attr('points', '-6,0 0,12 6,0')
      .attr('transform', (d: SelectLinesDatum) => `translate(${x(d.x)}, 0)`)
      .classed('hide', hide)
      .on('mousedown', (e: Event, d: SelectLinesDatum) => {
        e.stopPropagation();
        selection.setFreeEnd(d.type);
        selection.set('active', mapType);
      });

    root.exit().remove();
  }

  blinkCursor(): void {
    d3.select(this.el).selectAll('.map-cursor-line').classed('line-blink', true);
  }

  boxHeight(): number {
    const height = this.y.reduce(
      // translations by decimal amounts results in imprecise positioning for text, so we round.
      (total, className) => total + Math.ceil(this.components[className]!.boxHeight()),
      0
    );
    return Math.max(height, this.getMinSliceHeight());
  }

  // TODO: this function can be replace with the more generic 'boxHeightBetweenLayers'
  boxHeightToDNA(className: SliceComponentName): number {
    let dnaDist = 0;
    const [minIndex, maxIndex] = [this.yIndexMap.dna, this.yIndexMap[className]].sort((a, b) => a! - b!);
    for (const betweenComponentClass of this.y.slice(minIndex! + 1, maxIndex)) {
      dnaDist += Math.ceil(this.components[betweenComponentClass]!.boxHeight());
    }
    return dnaDist;
  }

  boxHeightBetweenLayers(
    higherLayerClassName: SliceComponentName,
    lowerLayerClassName: SliceComponentName
  ): number {
    let interLayerDist = 0;
    const [minIndex, maxIndex] = [
      this.yIndexMap[lowerLayerClassName]!,
      this.yIndexMap[higherLayerClassName]!,
    ].sort((a, b) => a - b);
    for (const betweenComponentClass of this.y.slice(minIndex + 1, maxIndex)) {
      interLayerDist += this.components[betweenComponentClass]!.boxHeight();
    }
    return interLayerDist;
  }

  /**
   * @returns {number} the distance from the top of the slice to below the DNA residues, consistent with the
   *                   region that is highlighted when making a selection
   */
  distanceFromSliceTopToBelowDNAResidues(): number {
    let dnaDist = 0;
    const [minIndex, maxIndex] = [0, this.yIndexMap.dna! + 1].sort((a, b) => a - b);
    for (const betweenComponentClass of this.y.slice(minIndex + 1, maxIndex)) {
      dnaDist += Math.ceil(this.components[betweenComponentClass]!.boxHeight());
    }
    return dnaDist;
  }

  adjustHeight(skipUpdateForAdjust: boolean = false): void {
    // For each thing that gets adjusted, be sure to see if the distance from dna has changed,
    // if anything between it and dna has changed, we need to rerender that thing.

    const updateForAdjust: {-readonly [key in keyof typeof SliceComponentName]?: boolean} = {};

    let nextHeight = 0;
    let additionalBottomMargin = 0;
    this.y.forEach((className, index) => {
      const selector = d3.select(this.el).selectAll(`g.${className}`);
      if (selector.node() == null) {
        return;
      }
      const match = /translate\(0,(.+)\)/.exec(selector.attr('transform'));
      const oldHeight = match != null && parseFloat(match[1]);
      selector.attr('transform', `translate(0,${nextHeight})`);

      if (oldHeight !== nextHeight && this.yDeps[className] != null && !skipUpdateForAdjust) {
        for (const depClassName of this.yDeps[className]) {
          updateForAdjust[depClassName] = true;
        }
      }

      const componentBoxHeight = Math.ceil(this.components[className]!.boxHeight());
      // For next iteration
      nextHeight += componentBoxHeight;
      if (componentBoxHeight !== 0) {
        // If the final visible component bleeds into the margin, ensure that there is sufficient margin for
        // it to bleed into. Note that we are assuming that in the case it is not the final component, that
        // the following component will have sufficient vMargin for our text to bleed into.
        additionalBottomMargin = this.components[className]!.bottomMarginBleed?.() ?? 0;
      }
    });
    nextHeight += additionalBottomMargin;

    for (const className of Object.keys(updateForAdjust)) {
      this.components[className as SliceComponentName]!.updateForAdjust();
    }

    nextHeight = Math.max(nextHeight, this.getMinSliceHeight());
    d3.select(this.el).attr('height', nextHeight);
    this.triggerHeightChange(nextHeight);
  }

  update(): void {
    if (this.dontUpdate) {
      return;
    }
    if (!this.hasRendered) {
      for (const comp of this.zComponents) {
        d3.select(this.el)
          .append('svg:g')
          .each(function setThis(): void {
            comp.setElement(this);
          });
      }
      for (const comp of this.rComponents) {
        comp.render(false);
      }
    } else {
      for (const comp of this.rComponents) {
        comp.update(false);
      }
    }
    this.hasRendered = true;

    // Since we just re-rendered all of the subcomponents, there is no reason to trigger an update
    // for each of the subcomponents.
    const skipUpdateForAdjust = true;
    this.adjustAndSelect(skipUpdateForAdjust);

    this.setSVGWidth();
  }

  updateComponent(componentName: SliceComponentName): void {
    // eslint-disable-next-line benchlint/no-in-operator
    if (componentName in this.components) {
      this.components[componentName]!.update();
    }
  }

  adjustAndSelect(skipUpdateForAdjust: boolean = false): void {
    this.adjustHeight(skipUpdateForAdjust);
    this.doSelect();
    if (this.trim != null) {
      this.doTrim();
    }

    const newBoxHeight = this.boxHeight();
    if (this.oldBoxHeight !== newBoxHeight) {
      this.oldBoxHeight = newBoxHeight;
      this.oldMaxBases = this.model.get('maxBases');
    }

    let bodySlice = d3
      .select(this.el)
      .selectAll('rect.body-slice')
      .data([{width: this.slice.get('width'), height: newBoxHeight}]);
    bodySlice = bodySlice.enter().insert('rect', ':first-child').classed('body-slice', true);
    bodySlice.exit().remove();
    bodySlice.attr('height', (d) => d.height).attr('width', (d) => d.width);
    addTestAttrsToD3Selection(bodySlice, {component: 'SliceView', element: 'bodySliceRectangle'});
  }

  override render(): SliceView {
    this.registerListeners();
    this.update();
    return this;
  }
}

SliceView.initClass();

/**
 * A read-only linear sliceview that does not require a polymer. Currently only used for Protein alignments.
 */
class BasicSliceView extends SliceView {
  override initialize(
    options: SliceViewOptions & {polymerResidues: string; forAlignmentPNGExport: boolean}
  ): void {
    this._polymerResidues = options.polymerResidues;
    this._forAlignmentPNGExport = options.forAlignmentPNGExport;
    super.initialize(options);
  }

  override getResidueSlice(): string {
    return sequtil.residueSlice(this._polymerResidues!, this.slice.start(), this.slice.end(), false);
  }

  override setSVGWidth(): void {
    // TODO(T7916): Revisit how type is accessed.
    const {fontSize, letterSpacing} = this.model.get('polymerLayout').aminoAcids.type;
    const type = new Type(fontSize, letterSpacing);
    const width = type.width(this.slice.end() - this.slice.start());
    const padding = letterSpacing;
    d3.select(this.el).attr('width', padding + width + 1);
  }

  override canEditSelection(): boolean {
    return false;
  }

  override openSelectionContextMenu(e: {preventDefault(): void; stopPropagation(): void}): void {
    // TODO(T7916): Implement
    e.preventDefault();
    e.stopPropagation();
  }

  override handleDoubleClick(e: {preventDefault(): void}): void {
    // TODO(T7916): Implement
    e.preventDefault();
  }

  override getPolymerLength(): number {
    return this._polymerResidues!.length;
  }
}

export {SliceView, BasicSliceView};
